import { useState, useEffect } from 'react';
import { fetchCases } from '../lib/cms';
import { MapPin, Tag, X, ZoomIn, Zap } from 'lucide-react';
import SEO from '../components/SEO';

export default function Cases() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [activeCase, setActiveCase] = useState(null);

  useEffect(() => {
    async function loadCases() {
      setLoading(true);
      const data = await fetchCases();
      setCases(data);
      setLoading(false);
    }
    loadCases();
  }, []);

  const categories = ['Alle', ...new Set(cases.map(item => item.category?.da || item.category).filter(Boolean))];

  const filteredCases = selectedCategory === 'Alle'
    ? cases
    : cases.filter(item => (item.category?.da || item.category) === selectedCategory);

  const casesSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Forside",
        "item": "https://detekniq.dk/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Cases & Referencer",
        "item": "https://detekniq.dk/cases"
      }
    ]
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Cases & Referencer"
        description="Se vores udvalgte cases og referencer inden for el, energi, varmepumper og sikring. Vi leverer professionelle løsninger i Kjellerup, Silkeborg, Viborg og omegn."
        schema={casesSchema}
      />
      {/* Header Banner */}
      <div className="bg-navy-950 py-16 text-white md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-wider text-energy-400">Vores Referencer</span>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl mt-3">
            Cases & Referencer
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-navy-200">
            Se et udvalg af de mange projekter, vi har udført for vores tilfredse kunder i Kjellerup, Silkeborg, Viborg og resten af Midtjylland.
          </p>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-wrap justify-center gap-2 border-b border-navy-100 pb-6">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`rounded-none px-5 py-2.5 text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-energy-500/20 ${
                selectedCategory === cat
                  ? 'bg-energy-600 text-white shadow-md'
                  : 'bg-navy-50 text-graphite-700 hover:bg-navy-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Cases Grid */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-navy-600 border-t-transparent"></div>
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="text-center py-24 text-graphite-500">
            Ingen cases fundet i denne kategori.
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCases.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveCase(item)}
                className="group text-left overflow-hidden rounded-none bg-white border border-navy-100 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-energy-500"
              >
                <div className="aspect-video overflow-hidden bg-navy-50 relative">
                  {item.images && item.images[0] ? (
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${item.images[0].replace('https://assets.auxodev.dk/', '')}`}
                      alt={item.title?.da || item.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-navy-900 text-white">
                      <Zap className="h-10 w-10 text-energy-500" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-navy-950/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <ZoomIn className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-energy-600">
                    <span className="flex items-center gap-1">
                      <Tag className="h-3.5 w-3.5" />
                      {item.category?.da || item.category}
                    </span>
                    <span className="flex items-center gap-1 text-graphite-500">
                      <MapPin className="h-3.5 w-3.5" />
                      {item.location?.da || item.location}
                    </span>
                  </div>
                  <h3 className="mt-3 text-lg font-bold text-navy-950 group-hover:text-energy-600 transition-colors">
                    {item.title?.da || item.title}
                  </h3>
                  <div
                    className="mt-3 text-sm text-graphite-600 line-clamp-3 leading-relaxed prose"
                    dangerouslySetInnerHTML={{ __html: item.text?.da || item.text }}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Case Detail Modal / Lightbox */}
      {activeCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/80 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-3xl rounded-none bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            {/* Close Button */}
            <button
              onClick={() => setActiveCase(null)}
              className="absolute top-4 right-4 z-10 rounded-none bg-navy-900/10 p-2 text-navy-950 hover:bg-navy-900/20 focus:outline-none focus:ring-2 focus:ring-energy-500"
              aria-label="Luk vindue"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Scrollable Content */}
            <div className="overflow-y-auto p-8">
              {/* Image Banner */}
              {activeCase.images && activeCase.images[0] && (
                <div className="aspect-video overflow-hidden rounded-none bg-navy-50 mb-6">
                  <img
                    src={activeCase.images[0]}
                    alt={activeCase.title?.da || activeCase.title}
                    className="h-full w-full object-cover"
                  />
                </div>
              )}

              {/* Meta details */}
              <div className="flex flex-wrap gap-4 items-center text-xs font-bold uppercase tracking-wider text-energy-600 mb-4">
                <span className="flex items-center gap-1 bg-energy-50 px-3 py-1.5 rounded-none">
                  <Tag className="h-3.5 w-3.5" />
                  {activeCase.category?.da || activeCase.category}
                </span>
                <span className="flex items-center gap-1 bg-navy-50 text-graphite-600 px-3 py-1.5 rounded-none">
                  <MapPin className="h-3.5 w-3.5" />
                  {activeCase.location?.da || activeCase.location}
                </span>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-extrabold text-navy-950 md:text-3xl">
                {activeCase.title?.da || activeCase.title}
              </h2>

              {/* Rich Text Description */}
              <div
                className="mt-6 prose max-w-none"
                dangerouslySetInnerHTML={{ __html: activeCase.text?.da || activeCase.text }}
              />

              {/* Additional Images Grid */}
              {activeCase.images && activeCase.images.length > 1 && (
                <div className="mt-8 border-t border-navy-100 pt-6">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-navy-950 mb-4">Flere billeder fra projektet</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {activeCase.images.slice(1).map((imgUrl, idx) => (
                      <div key={idx} className="aspect-video overflow-hidden rounded-none bg-navy-50">
                        <img
                          src={imgUrl}
                          alt={`${activeCase.title?.da || 'Case'} - Ekstra billede ${idx + 1}`}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
